import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Button,
    CheckBox,
    ChooseDropdown,
    DropdownSelectTrigger,
    Flex,
    HTMLDialog,
    Input,
    Scrollbar,
    Text
} from '@priority-software/priority-style-react';
import {useScrollWrapDimensions} from '../../../../../../hooks/customScroll/useScrollWrapDimensions';
import {DialogToolbarContent} from '../../../../../../components/Dialog/DialogToolbar/DialogToolbar';
import {
    getBpmDataByKey,
    getStatusDetails,
    getStatusEname,
    getStatusID,
    getStatusPos,
    getStatusProperties,
    getStatusStatProperties,
    setIsNewStatusFromReact
} from '../../../../../../_shared/GWTBridge/invoke/bpm/bpmMain';
import {bpmPropsAsync} from '../../../../../../services/bpmService';
import {dialogID} from './constants';
import {texts} from '../../texts';
import styles from './styles.module.scss';
import {isEnterKey} from '../../../../../../utils/isEnterKey';

type Props = {
    onClose: () => void;
    onSave: (status: string) => void;
    currentStatus?: string;
    showHiddenStatuses: () => void;
};

export const StatusDialog = (props: Props) => {
    const {onClose, onSave, showHiddenStatuses, currentStatus = ''} = props;
    const {setWrapRef, maxHeight: scrollMaxHeight} = useScrollWrapDimensions();
    const [name, setName] = useState(currentStatus);
    const [secondName, setSecondName] = useState(getStatusEname(currentStatus));
    const [properties, setProperties] = useState(getStatusProperties(currentStatus));
    const statusPos = useRef<number>(getStatusPos(currentStatus));
    const [statProperties, setStatProperties] = useState(getStatusStatProperties(currentStatus));
    const isPendingSave = useRef(false);
    const initialInputRef = useRef<HTMLInputElement>(null);
    const scrollRef = useRef<any>(null);
    const statusNameLimit = useRef(getBpmDataByKey('deslimit'));
    const statusENameLimit = useRef(getBpmDataByKey('edeslimit'));
    const isNewStatus = !currentStatus;
    const [disableSave, setDisableSave] = useState(false);

    const updateProperties = (name: string, checked: boolean) => {
        setProperties(
            properties.map(item => {
                if (item.title === name) {
                    return {
                        ...item,
                        val: checked
                    };
                }

                return item;
            })
        );
    };

    const onStatusSave = async () => {
        setDisableSave(true);

        if (isNewStatus) {
            setIsNewStatusFromReact(true);
        }

        const res = await bpmPropsAsync(
            {
                isNew: isNewStatus,
                statusId: getStatusID(currentStatus),
                statusName: name,
                statusDetails: getStatusDetails(currentStatus)
            },
            {
                value: secondName,
                pos: statusPos.current
            },
            properties,
            statProperties.map(item => ({pos: item.pos, val: item.val?.title?.toString()}))
        );

        if ((res as any).BPM[0].Message[0].messagetype === 'ok') {
            onSave(name);
        }
        setDisableSave(false);
    };

    const handleEnter = async (e: React.KeyboardEvent) => {
        if (isEnterKey(e) && isPendingSave.current === false) {
            isPendingSave.current = true;
            await onStatusSave();
            isPendingSave.current = false;
        }
    };

    const onStatPropChange = (item, selected, name) => {
        setStatProperties(data =>
            data.map(stat => {
                if (stat.title === name) {
                    return {
                        ...stat,
                        val: {id: item?.value, title: item?.label}
                    };
                }
                return stat;
            })
        );
    };

    const chooseStatsData = statProperties?.[0]?.chooseStats;
    const chooseStats = chooseStatsData ? chooseStatsData[Object.keys(chooseStatsData)[0]] : [];

    const chooseOptions = useMemo(() => {
        return (
            chooseStats
                ?.map(item => {
                    return {
                        value: item.id || undefined,
                        label: item.title
                    };
                })
                .filter(item => item.value) || []
        );
    }, [chooseStats]);

    const clearStatProp = (name: string) => {
        onStatPropChange({id: 0, title: null}, undefined, name);
    };

    useEffect(() => {
        if (initialInputRef.current) {
            initialInputRef.current.focus();
            scrollRef.current?.scrollToTop();
        }
    }, []);

    return (
        <HTMLDialog
            id={dialogID}
            componentClasses={styles.component}
            width={520}
            escClosable={true}
            isOpen={true}
            toolbarContent={
                <DialogToolbarContent
                    mainTitle={
                        currentStatus
                            ? texts.edit_status_dialog_title.replace('[X]', currentStatus)
                            : texts.new_status_dialog_title
                    }
                    helpText={
                        currentStatus ? texts.properties_help_text : texts.create_status_help_text
                    }
                    tooltipPortalID={dialogID}
                />
            }
            content={
                <div ref={setWrapRef} style={{height: '100%'}}>
                    <Scrollbar
                        autoHeight
                        autoHeightMax={scrollMaxHeight}
                        shiftVerticalScrollPositionX={15}
                        scrollbarRef={scrollRef}
                    >
                        <Flex
                            direction="column"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            style={{gap: 24}}
                        >
                            {isNewStatus ? (
                                <Input
                                    label={texts.status_name_label}
                                    required
                                    size="xlarge"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    onKeyUp={handleEnter}
                                    inputRef={initialInputRef}
                                    placeholder={texts.name_input_placeholder}
                                    maxLength={statusNameLimit.current}
                                />
                            ) : null}

                            <Input
                                label={texts.status_ename_label}
                                size="xlarge"
                                value={secondName}
                                onChange={e => setSecondName(e.target.value)}
                                onKeyUp={handleEnter}
                                inputRef={isNewStatus ? undefined : initialInputRef}
                                placeholder={texts.name_input_placeholder}
                                maxLength={statusENameLimit.current}
                            />

                            {statProperties.map(item => {
                                return (
                                    <div style={{width: '100%'}} key={item.title}>
                                        <Text skin="label-1">{item.title}</Text>
                                        <ChooseDropdown
                                            name={item.title}
                                            data={chooseOptions}
                                            trigger={
                                                <DropdownSelectTrigger
                                                    size="xlarge"
                                                    clearable={!!item?.val?.id}
                                                    onClearValue={() => clearStatProp(item.title)}
                                                />
                                            }
                                            value={item?.val?.id || undefined}
                                            onChange={onStatPropChange}
                                        />
                                    </div>
                                );
                            })}

                            {properties.map(item => (
                                <CheckBox
                                    key={item.title}
                                    checked={item.val}
                                    onChange={e => updateProperties(item.title, !item.val)}
                                >
                                    <Text skin="paragraph-2" tag={'span'}>
                                        {item.title}
                                    </Text>
                                </CheckBox>
                            ))}
                        </Flex>
                    </Scrollbar>
                </div>
            }
            footer={
                <Flex justifyContent="flex-end" alignItems="center" style={{gap: 24}}>
                    {isNewStatus && showHiddenStatuses ? (
                        <Button
                            size="medium"
                            onClick={showHiddenStatuses}
                            skin="text"
                            style={{textDecoration: 'underline'}}
                        >
                            {texts.hidden_statuses_label}
                        </Button>
                    ) : null}
                    <Button size="medium" wideButton onClick={onClose} skin="secondary">
                        {texts.status_dialog_btn_cancel}
                    </Button>
                    <Button
                        size="medium"
                        wideButton
                        onClick={onStatusSave}
                        skin="primary"
                        disabled={!Boolean(name) || disableSave}
                    >
                        {texts.status_dialog_btn_save}
                    </Button>
                </Flex>
            }
            onClose={onClose}
            size="large"
        />
    );
};
